import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Star } from "lucide-react";

import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

// Press articles — add new entries to grow the section over time.
// `featured` and `exclusive` control the small gold badges.
const ARTICLES = [
  {
    publication: "Ouro Verde FM",
    location: "Brazil",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/7uh8kq9r_Ouro%20verde%20brasil%20.jpeg",
    title:
      "Irwan Craw: A Nova Ponte Eletrônica entre a França e a América Latina",
    date: "May 5, 2026",
    excerpt:
      "Few artists translate the fusion of cultures as organically as Irwan Craw — a French producer whose Latin-infused electronic sound is becoming one of the most compelling voices of the emerging scene.",
    url: "https://ouroverdefm.com/noticia/2560365/irwan-craw-a-nova-ponte-eletronica-entre-a-franca-e-a-america-latina",
    featured: true,
    exclusive: true,
  },
  {
    publication: "Beat for Beat",
    location: "Brazil",
    logo: "https://beatforbeat.com.br/site/wp-content/uploads/2024/07/Logo-Site.png",
    title:
      "Irwan Craw apresenta o single ‘Noche Eterna’ pela Sunwave Music",
    date: "May 13, 2026",
    excerpt:
      "The French producer transforms 'Noche Eterna' into an expanded, cinematic dancefloor experience — Latin heat, hypnotic grooves and neon-lit atmospheres crafted to carry entire nights.",
    url: "https://beatforbeat.com.br/site/irwan-craw-apresenta-o-single-noche-eterna-pela-sunwave-music/",
    featured: true,
    exclusive: false,
  },
  {
    publication: "ElFiesta",
    location: "Spain",
    logo: null,
    title:
      "Irwan Craw irrumpe en la escena Latin EDM con “Noche Eterna (Summer Remix)”",
    date: "May 2026",
    excerpt:
      "The French producer bursts onto the Latin EDM scene with a summer anthem — Latin house rhythms, club energy and a modern identity already earning support from European radios and online platforms.",
    url: "https://www.elfiesta.es/index.php/noticias/12231-irwan-craw-irrumpe-en-la-escena-latin-edm-con-noche-eterna-summer-remix-un-track-que-mezcla-latin-house-energia-club-y-una-identidad-moderna",
    featured: true,
    exclusive: false,
  },
  {
    publication: "EDM Rebel",
    location: "International",
    logo: null,
    title:
      "Irwan Craw releases “Noche Eterna (Remix)” — a Latin Tech House track blending club energy and summer vibes",
    date: "April 27, 2026",
    excerpt:
      "French DJ and producer Irwan Craw continues to build momentum on the electronic music scene, balancing accessibility with club energy inside the evolving Latin EDM landscape.",
    url: "https://edmrebel.com/irwan-craw-releases-noche-eterna-remix-a-latin-tech-house-track-blending-club-energy-and-summer-vibes/",
    featured: true,
    exclusive: false,
  },
];

const Badge = ({ label, icon: Icon, accent = false }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.28em] ${
      accent
        ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold-hi)]"
        : "border-[color:var(--gold)]/40 bg-[#0a0a0a]/70 text-ink/85"
    }`}
  >
    {Icon && <Icon size={10} className="text-gold" />}
    {label}
  </span>
);

const PressCard = ({ a, i }) => {
  return (
    <motion.article
      variants={fadeUp}
      data-testid={`press-card-${i}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2/70 backdrop-blur-md transition-all duration-700 hover:border-[color:var(--gold)]/60 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]"
    >
      {/* Cover / logo band */}
      <div className="relative border-b border-[color:var(--line)] bg-gradient-to-br from-[color:var(--gold)]/[0.05] to-transparent p-6 md:p-8">
        <span className="pointer-events-none absolute right-4 top-3 font-editorial text-lg text-[color:var(--gold-hi)]/50">
          0{i + 1}
        </span>

        <div className="flex items-center gap-4">
          {a.logo ? (
            <div
              data-testid={`press-logo-${i}`}
              className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/95 ring-1 ring-[color:var(--gold)]/25 shadow-[0_0_18px_rgba(201,169,97,0.12)]"
            >
              <img
                src={a.logo}
                alt={`${a.publication} logo`}
                className="h-full w-full object-contain p-1"
                loading="lazy"
              />
            </div>
          ) : (
            <div
              aria-hidden
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-[color:var(--line-strong)] bg-bg font-display text-lg font-black text-[color:var(--gold-hi)]"
            >
              {a.publication
                .split(" ")
                .map((w) => w[0])
                .slice(0, 2)
                .join("")}
            </div>
          )}
          <div>
            <p
              data-testid={`press-publication-${i}`}
              className="font-display text-base md:text-lg font-bold uppercase tracking-[0.08em] text-ink leading-tight"
            >
              {a.publication}
            </p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-muted mt-1">
              {a.location}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="mt-5 flex flex-wrap gap-2">
          {a.exclusive && (
            <Badge label="Exclusive Interview" icon={Star} accent />
          )}
          {a.featured && !a.exclusive && <Badge label="Featured" accent />}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-6 md:p-8">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted">
          <Calendar size={10} className="text-gold" />
          <span data-testid={`press-date-${i}`}>{a.date}</span>
        </div>

        <h3
          data-testid={`press-title-${i}`}
          className="mt-4 font-display text-xl md:text-2xl font-bold leading-snug tracking-tight text-ink"
        >
          {a.title}
        </h3>

        <p
          data-testid={`press-excerpt-${i}`}
          className="mt-4 text-sm md:text-[15px] text-muted leading-relaxed"
        >
          {a.excerpt}
        </p>

        <div className="mt-auto pt-6">
          <a
            data-testid={`press-cta-${i}`}
            href={a.url}
            target="_blank"
            rel="noreferrer noopener"
            className="group/btn relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-[color:var(--gold)]/50 bg-transparent px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)] transition-all duration-500 hover:border-[color:var(--gold)]"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 -translate-x-full bg-[color:var(--gold)] transition-transform duration-500 ease-out group-hover/btn:translate-x-0"
            />
            <span className="relative transition-colors duration-500 group-hover/btn:text-[#0a0a0a]">
              Read Article
            </span>
            <ArrowUpRight
              size={12}
              className="relative transition-all duration-500 group-hover/btn:text-[#0a0a0a] group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
            />
          </a>
        </div>
      </div>

      {/* Hover glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(201,169,97,0.14), transparent 55%)",
        }}
      />
    </motion.article>
  );
};

const EmptyState = () => (
  <div className="rounded-sm border border-dashed border-[color:var(--line-strong)] bg-bg-2/50 p-14 text-center">
    <p className="font-editorial text-2xl text-[color:var(--gold-hi)]/90">
      Press features arriving soon.
    </p>
    <p className="mt-3 max-w-md mx-auto text-sm text-muted leading-relaxed">
      For editorial requests, features and interviews — reach out through the
      booking form below.
    </p>
  </div>
);

export const PressMedia = () => {
  return (
    <section
      id="press-media"
      data-testid="press-media-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 55% 40% at 20% 15%, rgba(201,169,97,0.10), transparent 60%), radial-gradient(ellipse 55% 40% at 85% 90%, rgba(201,169,97,0.08), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        {/* Header */}
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 06 — Editorial
            </p>
            <h2
              data-testid="press-media-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Press &{" "}
              <span className="font-editorial text-gold-hi">Media</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            Discover official media coverage, interviews and featured articles
            highlighting Irwan Craw's artistic journey and growing presence
            within the international electronic music scene.
          </motion.p>
        </motion.div>

        {/* Intro */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          data-testid="press-media-intro"
          className="max-w-3xl font-editorial text-xl md:text-2xl text-ink/90 leading-snug mb-10 md:mb-14"
        >
          Through interviews, reviews and editorial features, international
          music publications continue to highlight Irwan Craw's unique blend of{" "}
          <span className="text-gold-hi">Afro House</span>,{" "}
          <span className="text-gold-hi">Organic House</span> and deep melodic
          soundscapes.
        </motion.p>

        {/* Grid */}
        {ARTICLES.length > 0 ? (
          <motion.div
            variants={stagger(0.12, 0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          >
            {ARTICLES.map((a, i) => (
              <PressCard key={a.url} a={a} i={i} />
            ))}
          </motion.div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};
